import { useCallback, useEffect, useRef, useState } from "react";
import { useBeforeUnload } from "react-use";
import { useLatestValue } from "Hooks";
import { handleRouterBack } from "../modules/utils";

const useUnsavedChanges = (hasUnsavedChanges, router, route) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const isProceedingRef = useRef(false);
  const hasUnsavedChangesRef = useLatestValue(hasUnsavedChanges);
  const nextLocationRef = useRef(null);

  useBeforeUnload(hasUnsavedChanges);

  useEffect(() => {
    if (!router || !route) {
      return;
    }

    const unhook = router.setRouteLeaveHook(route, nextLocation => {
      if (isProceedingRef.current) {
        isProceedingRef.current = false;
        return handleRouterBack(router, nextLocation);
      }

      if (!hasUnsavedChangesRef.current)
        return handleRouterBack(router, nextLocation);

      nextLocationRef.current = nextLocation;
      setIsBlocked(true);

      return false;
    });

    return () => {
      unhook();
    };
  }, [router, route, hasUnsavedChangesRef]);

  const reset = useCallback(() => {
    setIsBlocked(false);
  }, []);

  const proceed = useCallback(() => {
    const nextLocation = nextLocationRef.current;
    nextLocationRef.current = null;
    setIsBlocked(false);

    if (!nextLocation) return;

    isProceedingRef.current = true;
    const { action, pathname, query, state } = nextLocation;

    if (action === "REPLACE") router.replace({ pathname, query, state });
    else if (action === "POP") router.goBack();
    else router.push({ pathname, query, state });
  }, [router]);

  return { isBlocked, proceed, reset };
};

export default useUnsavedChanges;
